/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { Box } from "@material-ui/core";
import { useI18n } from "hooks/i18n/i18n";
import React, {
  useState,
  useEffect,
  useCallback,
  ComponentProps,
  FC,
  SVGAttributes,
} from "react";
import { useDispatch } from "react-redux";

import {
  CommentFilterType,
  streamingActions,
  StreamingType,
} from "../../modules/streaming";
import icon_comment_none from "../../designs/images/icon_comment_none.svg";
import { authState } from "modules/auth";
import { StreamingCommentItemV2 } from "./StreamingCommentItemV2";
import { Store, useSelector } from "../../store";

import type { DisplayComment } from "./applyBlockedComments";
import type { Timestamp } from "@google-cloud/firestore";
import type { THEOPlayer } from "@spwn/types/external";
import type { ProductData } from "@spwn/types/firebase/firestore";
import type { EventInfo } from "../../utility";

const getMsgListElement = () => {
  const innerElement = document.getElementById("msg-list-layout");
  const listElement = document.getElementById("msg-list-inner");
  return { innerElement, listElement };
};
/**
 * 取得したコメント欄のエレメントを基に、コメント欄の一番下までの高さを取得する
 * @param innerElement
 * @param listElement
 */
const getMsgListValue = (
  innerElement: HTMLElement,
  listElement: HTMLElement
) => {
  const msgAreaHeight = innerElement.scrollHeight;
  const msgDispleyFlame = listElement.clientHeight;
  const bottomPosition = msgAreaHeight - msgDispleyFlame;
  return bottomPosition;
};
/**
 * コメント欄の表示位置を一番下まで移動する
 */
const msgScrollBottom = () => {
  const { innerElement, listElement } = getMsgListElement();
  if (!innerElement || !listElement) return;
  const bottomPosition = getMsgListValue(innerElement, listElement);
  listElement.scrollTop = bottomPosition;
};

type Props = {
  eventStart: Timestamp;
  streamingType: StreamingType;
  player: THEOPlayer;
  eventInfo: EventInfo;
  displayComments: DisplayComment[];
  eventGoodsMap: { [goodsId: string]: ProductData };
  user: authState["user"];
  isChatAdmin: boolean;
  isDarkModeEnabled: boolean;
  THEOPlayerElementHeight?: number;
  commentFilterState: CommentFilterType;
  banUser: ComponentProps<typeof StreamingCommentItemV2>["onClickBanUser"];
  banComment: ComponentProps<
    typeof StreamingCommentItemV2
  >["onClickBanComment"];
  blockUser: ComponentProps<typeof StreamingCommentItemV2>["onClickBlockUser"];
  deleteComment: ComponentProps<
    typeof StreamingCommentItemV2
  >["msgDeleteFunction"];
  // コメント欄に重ねて出す。position: absolute の基準がこのコンポーネントのルート。
  blockedUserToast?: React.ReactNode;
};

export const StreamingCommentLayout: React.FC<Props> = (props) => {
  const { t } = useI18n();
  const giftItemMap = useSelector(
    (state: Store) => state.streaming.giftItemMap
  );

  const [autoScroll, setAutoScroll] = useState<boolean>(true);

  const [menuOpenCommentId, setMenuOpenCommentId] = useState<
    string | undefined
  >(undefined);

  // IDで重複を除去
  const uniqueCommentsMap = new Map(
    props.displayComments.map((c) => [c._id, c])
  );
  const uniqueComments = Array.from(uniqueCommentsMap.values());

  // _id はポーリング時に自分で投稿した直後のコメントには無い。id 同士を比べるだけだと
  // メニューを開いていない (menuOpenCommentId === undefined) ときに一致してしまう。
  const isMenuOpenComment = (commentId: string | undefined) =>
    menuOpenCommentId !== undefined && commentId === menuOpenCommentId;

  // 開いたままコメントが消えたときに止まりっぱなしにならないよう、表示中の
  // コメントに残っているかで判定する。
  const isMenuOpen =
    menuOpenCommentId !== undefined && uniqueCommentsMap.has(menuOpenCommentId);

  useEffect(() => {
    // scroll to bottom when auto scroll mode and displayComments is updated
    if (autoScroll && !isMenuOpen) {
      msgScrollBottom();
    }
  }, [
    autoScroll,
    isMenuOpen,
    props.displayComments,
    props.THEOPlayerElementHeight,
  ]);

  const dispatch = useDispatch();
  useEffect(() => {
    // 一覧が更新されると、表示上限を超えたぶんが古い順に捨てられて中身が上へ詰め
    // 上がる。Chrome は scroll anchoring で表示位置を補正するが、iOS Safari は
    // 補正しないため、スクロールを止めるだけではメニューのアンカーにしている
    // コメントが上端から消える。手動で上へスクロールしたときと同じく取得自体を
    // 止める。
    dispatch(streamingActions.setPauseCommentFetch(!autoScroll || isMenuOpen));
    return () => {
      dispatch(streamingActions.setPauseCommentFetch(false));
    };
  }, [autoScroll, isMenuOpen, dispatch]);

  const changeScrollState = useCallback(() => {
    const { innerElement, listElement } = getMsgListElement();
    // HOTFIX: テストの場合は onscroll を無視したい
    if (process.env.NODE_ENV !== "test") {
      // @ts-expect-error TS18047
      listElement.onscroll = () => {
        if (!innerElement || !listElement) return;
        const bottomPosition = getMsgListValue(innerElement, listElement);
        const scrollValue = listElement.scrollTop;
        // 1pxの誤差を許容する
        setAutoScroll(bottomPosition - scrollValue <= 1);
      };
    }
  }, []);

  useEffect(() => {
    changeScrollState();
    msgScrollBottom();
  }, [changeScrollState]);

  /**
   * ギフトアイテムの金額リストを作成する
   */
  const giftAmountList: number[] = [];
  if (giftItemMap) {
    Object.keys(giftItemMap).forEach((key) => {
      // @ts-expect-error TS2532
      const value = giftItemMap[key].values.paid;
      giftAmountList.push(value);
    });
    giftAmountList.sort((a, b) => a - b);
  }

  return (
    <Box
      position={"relative"}
      display={"flex"}
      flexDirection={"column"}
      height={"100%"}
      paddingBottom={"12px"}
      borderBottom={"1px solid #00000014"}
      borderColor={
        props.isDarkModeEnabled
          ? "dark.border.tertiary"
          : "light.border.tertiary"
      }
    >
      {/* コメント表示エリア */}
      <Box
        id={"msg-list-inner"}
        css={css`
          flex: 1 1 auto;
          width: 100%;
          height: 120px;
          overflow-y: auto;
          overscroll-behavior: none;
          ${props.isDarkModeEnabled
            ? "scrollbar-color: #5c5c5c #333333;"
            : undefined}
        `}
      >
        {props.commentFilterState === "none" ? (
          //中央に寄せるためにlayoutのBoxを分けている
          <Box
            id={"msg-list-layout"}
            height={"100%"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <Box
              display={"flex"}
              flexDirection={"column"}
              height={"100%"}
              paddingTop={"12px"}
              alignItems={"center"}
              justifyContent={"center"}
              fontSize={"14px"}
              color={props.isDarkModeEnabled ? "#FFFFFF66" : "#00000066"}
            >
              <img
                src={icon_comment_none}
                alt=""
                aria-label={t("streaming.comment.noCommentIconAriaLabel")}
                css={css``}
              />
            </Box>
          </Box>
        ) : (
          <Box id={"msg-list-layout"}>
            {uniqueComments.map((el) => {
              const isCommentUser = el.uid === props.user.uid;

              return (
                <StreamingCommentItemV2
                  key={el._id}
                  message={el}
                  isCommentUser={isCommentUser}
                  isChatAdmin={props.isChatAdmin}
                  isDarkModeEnabled={props.isDarkModeEnabled}
                  eventGoodsMap={props.eventGoodsMap}
                  giftAmountList={giftAmountList}
                  isMenuOpen={isMenuOpenComment(el._id)}
                  onOpenMenu={() => setMenuOpenCommentId(el._id)}
                  onCloseMenu={() => setMenuOpenCommentId(undefined)}
                  onClickBanUser={props.banUser}
                  onClickBanComment={props.banComment}
                  onClickBlockUser={props.blockUser}
                  msgDeleteFunction={props.deleteComment}
                />
              );
            })}
          </Box>
        )}
      </Box>

      {/* 最新のコメントに戻るボタン */}
      <Box
        className={autoScroll ? "hide" : ""}
        css={css`
          position: absolute;
          bottom: 10px;
          right: 50%;
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          color: ${props.isDarkModeEnabled ? "#000000" : "#ffffff"};
          font-size: 12px;
          font-weight: bold;
          background-color: ${props.isDarkModeEnabled ? "#ffffff" : "#000000"};
          border-radius: 8px;
          transform: translateX(50%);
          visibility: visible;
          opacity: 1;
          transition: 0.3s;
          cursor: pointer;
          box-shadow:
            0 2px 2px 0 rgba(0, 0, 0, 0.14),
            0 1px 5px 0 rgba(0, 0, 0, 0.12),
            0 3px 1px -2px rgba(0, 0, 0, 0.2);
          &.hide {
            visibility: hidden;
            opacity: 0;
          }
        `}
        onClick={() => setAutoScroll(true)}
      >
        <ArrowIcon width={"14px"} height={"14px"} />
        <Box>{t("streaming.comment.latest")}</Box>
      </Box>

      {props.blockedUserToast}
    </Box>
  );
};

const ArrowIcon: FC<SVGAttributes<SVGElement>> = (props) => {
  return (
    <svg viewBox="0 0 12 12" {...props}>
      <path d="M10 6L9.295 5.295L6.5 8.085V2H5.5V8.085L2.71 5.29L2 6L6 10L10 6Z" />
    </svg>
  );
};
